import { Component, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ActionSheetController, ViewWillLeave } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PaymentsBasePage } from '@payments/payments-shared/payments-base/payments-base.page';
import { PaymentsPaymentService } from '@payments/services/payments-payment.service';
import { PaymentsShareViaEmailService } from '@payments/services/payments-share-via-email.service';
import { PaymentsService } from '@payments/services/payments.service';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { AppService } from '@shared/services/app.service';
import {
  InlineToast,
  InlineToastService,
} from '@shared/services/inline-toast/inline-toast.service';
import {
  CustomerBankAccount,
  CustomerReview,
  PaymentSummary,
  PaymentTransactionsViewModel,
} from '@usf/payment-types';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PaymentConfirmationService } from './services/payment-confirmation.service';
import {
  PaymentConfirmationAnalyticsService,
  PaymentStatus,
} from './tracking/analytics/payment-confirmation-analytics.service';

export interface PaymentConfirmationPageVM {
  breadcrumbs?: Crumb[];
  selectedCustomersAmount?: number;
  customers?: CustomerReview[];
  transactions: PaymentTransactionsViewModel[];
  transactionsWithErrors: PaymentTransactionsViewModel[];
  overallCustomerBankAccounts: CustomerBankAccount[];
  totalAmount: number;
  isLoading: boolean;
  postingDate: string;
  shareFlag?: boolean;
  showOrderNumber?: boolean;
}

@Component({
  selector: 'app-payment-confirmation',
  templateUrl: './payment-confirmation.page.html',
  styleUrls: ['./payment-confirmation.page.scss'],
})
export class PaymentConfirmationPage
  extends PaymentsBasePage
  implements ViewWillLeave
{
  vm$: Observable<PaymentConfirmationPageVM>;
  pageText = {
    title: '',
  };
  paymentSummary: PaymentSummary;
  toast: InlineToast;
  toast$: Subscription;
  Capacitor = Capacitor;
  destroyed$ = new Subject<void>();

  constructor(
    readonly platformService: PlatformService,
    private readonly panAppState: PanAppState,
    private readonly paymentConfirmationService: PaymentConfirmationService,
    private readonly paymentConfirmationAnalytics: PaymentConfirmationAnalyticsService,
    private actionSheetController: ActionSheetController,
    private inlineToastService: InlineToastService,
    private translate: TranslateService,
    private paymentsPaymentService: PaymentsPaymentService,
    public readonly paymentsService: PaymentsService,
    public router: Router,
    public elementRef: ElementRef,
    private appService: AppService,
    readonly route: ActivatedRoute,
    readonly paymentsShareViaEmailService: PaymentsShareViaEmailService,
  ) {
    super(router, route, paymentsService);
  }

  async ionViewWillEnter() {
    super.ionViewWillEnter();
    this.paymentConfirmationService
      .canActivate()
      .pipe(takeUntil(this.destroyed$))
      .subscribe();
    this.panAppState.setPageTitle(
      this.translate.instant(PageTitle.paymentConfirmation),
    );
    this.vm$ = this.paymentConfirmationService.getPayment$();
    this.vm$
      .pipe(
        filter(
          vm =>
            vm.transactions.length > 0 || vm.transactionsWithErrors.length > 0,
        ),
        take(1),
      )
      .subscribe(vm => {
        this.setPaymentSummary(vm);
        this.setTranslateText(vm);
        if (vm.transactionsWithErrors.length > 0) {
          this.setInlineErrorToast();
        }
        this.paymentConfirmationAnalytics.trackPageLoad({
          fileCount: this.paymentSummary.transactionAmount,
          totalAmount: this.paymentSummary.totalAmount,
          paymentStatus:
            vm.transactions.length > 0
              ? vm.transactionsWithErrors.length > 0
                ? PaymentStatus.partSuccess
                : PaymentStatus.success
              : PaymentStatus.failed,
          customerCount: this.paymentSummary.customerAmount,
        });
      });
  }

  ionViewDidEnter() {
    this.appService.getCustomFeedback(
      'payment-submitted',
      false,
      environment.usabillaCampaingIdPaymentSubmitted,
    );
  }

  setInlineErrorToast() {
    this.toast$ = this.inlineToastService
      .getInlineToasts$()
      .subscribe(toast => {
        this.toast = toast;
      });

    this.inlineToastService.publishError(
      'i18n.payments.paymentConfirmation.toastErrorMessage',
    );
  }

  setTranslateText(vm: PaymentConfirmationPageVM) {
    this.pageText.title = this.translate.instant(
      'i18n.payments.paymentConfirmation.paymentSubmitted',
    );
    if (vm.transactions.length === 0) {
      this.pageText.title = this.translate.instant(
        'i18n.payments.paymentConfirmation.paymentAttempted',
      );
    }
  }

  setPaymentSummary(vm: PaymentConfirmationPageVM) {
    this.paymentSummary = this.paymentsPaymentService.createPaymentSummary(
      vm.postingDate,
      vm.customers.length,
      vm.transactions,
    );
  }

  async openMobileActionSheet(shareFlag: boolean) {
    const buttons = [];
    buttons.push({
      text: this.translate.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });

    buttons.push({
      text: this.translate.instant('i18n.common.download'),
      handler: () => {
        this.downloadPaymentConfirmation();
      },
    });

    if (shareFlag) {
      buttons.push({
        text: this.translate.instant('i18n.common.share'),
        handler: () => {
          this.sharePaymentConfirmation();
        },
      });
    }

    const actionSheet = await this.actionSheetController.create({
      mode: 'ios',
      buttons,
      cssClass: 'action-sheet-touch',
    });
    await actionSheet.present();
  }

  sharePaymentConfirmation(viaEmail = false) {
    this.vm$.pipe(take(1)).subscribe(vm => {
      const paymentData = {
        paymentSummary: this.paymentSummary,
        customers: this.filterTransactionWithNoFailed(vm.customers),
      };
      viaEmail
        ? this.paymentsShareViaEmailService.showShareViaEmailModal(paymentData)
        : this.paymentConfirmationService.sharePaymentConfirmation(paymentData);
    });
  }

  printPaymentConfirmation() {
    this.vm$.pipe(take(1)).subscribe(vm => {
      this.paymentConfirmationService.printPaymentConfirmation({
        paymentSummary: this.paymentSummary,
        customers: this.filterTransactionWithNoFailed(vm.customers),
      });
    });
  }

  downloadPaymentConfirmation() {
    this.vm$.pipe(take(1)).subscribe(vm => {
      this.paymentConfirmationService.downloadPaymentConfirmation({
        paymentSummary: this.paymentSummary,
        customers: this.filterTransactionWithNoFailed(vm.customers),
      });
    });
  }

  filterTransactionWithNoFailed(customer: CustomerReview[]) {
    return customer.map(customer => ({
      ...customer,
      transactions: customer.transactions.filter(
        transaction => !transaction.hasError,
      ),
    }));
  }

  ionViewWillLeave(): void {
    this.clearAll();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.toast$?.unsubscribe();
  }

  goBackToPaymentCenter() {
    this.router.navigate([PATHS.PAYMENTS]);
  }
}
